<template>
  <main>
    <b-container class="mt-4">
      <loading-spinner v-if="loading" />
      <router-view v-if="hasUser" />
    </b-container>
    <section
      v-if="isSelectingStudySite && !loading"
      id="studySiteSelection"
      class="py-4 customPaddingInline"
    >
      <div class="nav-style">
        <b-button class="pl-0" variant="link" @click="redirectBack">
          <b-icon class="font-weight-bold" icon="chevron-left"></b-icon>
          Back
        </b-button>
      </div>
      <div class="headerStyle mt-2">
        Manage Application Access for
        <span style="color: #5455a9">{{ userDetails.firstName + ' ' + userDetails.lastName }}</span>
      </div>
      <div class="textStyle mt-2">
        Use this page as a single point to manage all personal access to the external systems. Any
        change to your access will need to undergo evaluation and usually takes 1 to 3 days to be
        fully processed.
      </div>
      <section class="mt-5">
        <b-row align-v="center">
          <b-col>
            <b-card class="cardStyle" no-body>
              <b-tabs
                v-model="tabIndex"
                active-nav-item-class="bg-magnolia border border-white"
                content-class="mt-5"
                fill
              >
                <b-tab
                  :title-link-class="linkClass(0)"
                  active
                  title="Select Study - Site"
                  @click="reset()"
                >
                  <b-row class="px-3 mt-4">
                    <b-col class="pt-2" cols="5"> Study ID</b-col>
                    <b-col cols="7">
                      <multiselect
                        v-model="studyId"
                        :disabled="loading"
                        :options="studyOptions"
                        class="rounded-pill"
                        placeholder="Select a Study ID"
                      >
                      </multiselect>
                    </b-col>
                  </b-row>
                  <b-row class="my-4 px-3">
                    <b-col class="pt-2" cols="5"> Site #</b-col>
                    <b-col cols="7">
                      <multiselect
                        v-model="siteId"
                        :disabled="!studyId"
                        :options="siteOptions"
                        class="rounded-pill"
                        placeholder="Select a Site"
                      >
                      </multiselect>
                    </b-col>
                  </b-row>
                </b-tab>
                <b-tab :title-link-class="linkClass(1)" title="Not Listed" @click="reset()">
                  <b-row class="mb-5 mx-3">
                    <b-col>
                      <b-form-input
                        ref="Study"
                        v-model="studyId"
                        class="rounded-pill"
                        placeholder="Study ID"
                      ></b-form-input>
                    </b-col>
                    <b-col
                      ><b-form-input
                        ref="Site"
                        v-model="siteId"
                        class="rounded-pill"
                        placeholder="Site"
                      ></b-form-input></b-col></b-row
                ></b-tab>
              </b-tabs>
            </b-card>
          </b-col>
        </b-row>
      </section>
      <section v-if="isSelectingStudySite" id="testingRedirect" class="mt-5">
        <b-button
          :disabled="disableButton"
          class="custom-float-right mt-3 px-4 manage-access-button-text"
          pill
          type="submit"
          variant="primary"
          @click="next"
          >Next - Access Manager
          <div v-if="nextButtonLoading" style="display: inline">
            <b-spinner small></b-spinner>
            <span class="sr-only">Loading...</span>
          </div>
        </b-button>
      </section>
    </section>
  </main>
</template>

<script>
  import { mapGetters } from 'vuex';

  export default {
    name: 'select-study-site',
    data() {
      return {
        tabIndex: 0,
        studyOptions: [],
        loading: false,
        nextButtonLoading: false,
        studySites: [],
        siteId: null,
        studyId: null,
        siteOptions: [],
      };
    },
    watch: {
      studyId(newValue, oldValue) {
        if (this.studyId !== null && newValue !== oldValue && this.tabIndex === 0) {
          this.siteId = null;
          this.getSiteOptions();
        }
      },
    },
    created() {
      this.initialize();
    },
    computed: {
      ...mapGetters(['userDetails']),
      isSelectingStudySite() {
        return this.$route.name === 'application-access-manager-select-study-site';
      },
      hasUser() {
        return Object.keys(this.userDetails).length !== 0;
      },
      disableButton() {
        return !!(this.studyId === null || this.siteId === null);
      },
    },
    methods: {
      initialize() {
        this.loading = true;
        let endpoints = [
          `/api/get-studies?current_status=ONGOING,PROPOSED,PLANNED&compact=1&include_sites=1&exclude_studies_without_sites=1`,
        ];
        if (!this.hasUser) {
          endpoints.push(`/api/get-person-details?personId=${this.$route.params.personId}`);
        }
        Promise.allSettled(endpoints.map(endpoint => axios.get(endpoint)))
          .then(
            axios.spread((...allData) => {
              let [studySites, user] = allData;
              if (studySites.status === 'fulfilled' && studySites.value.status !== 204) {
                this.setStudySites(studySites.value.data['items']);
              }
              if (user !== undefined && user.status === 'fulfilled' && user.value.status !== 204) {
                this.setUser(user.value.data.data);
              }
              this.loading = false;
            }),
          )
          .catch(error => {
            console.error(error);
            this.loading = false;
          });
      },
      getSiteOptions() {
        let studySites = this.studySites.find(element => element['studyId'] === this.studyId);
        this.siteOptions = studySites['sites'].sort((a, b) => a.localeCompare(b));
      },
      setStudySites(items) {
        this.studySites = items;
        this.studyOptions = this.studySites.map(element => {
          return element['studyId'];
        });
        this.studyOptions.sort((a, b) => a.localeCompare(b));
      },
      async setUser(user) {
        if (!this.hasUser) {
          this.$store.commit('setUserDetails', {
            firstName: user.firstName,
            lastName: user.lastName,
            fullName: user.firstName + ' ' + user.lastName,
            emails: user.emails,
            selectedEmail: user.emails.length > 0 ? user.emails[0] : null,
            phoneNumber: '', //TODO check if this is needed
            personId: this.$route.params.personId,
            roles: user.roles ?? null, //TODO check if this is needed
          });
        }
      },
      async next() {
        let studySiteExists = false;
        if (this.tabIndex === 1) {
          this.nextButtonLoading = true;
          let params = {
            study_id: this.studyId,
            site_number: this.siteId,
          };
          let response = await axios.get(`/api/get-enrollment-oversight-sites`, {
            params: params,
          });
          if (response.status === 200) {
            studySiteExists = response.data.items.length > 0;
          }
        } else {
          studySiteExists = true;
        }
        this.nextButtonLoading = false;
        if (studySiteExists) {
          await this.$router
            .push({
              name: 'application-access-manager-create-request',
              params: {
                studyId: this.studyId,
                siteId: this.siteId,
              },
            })
            .then(() => {
              this.reset();
            });
        } else {
          this.$bvToast.toast(
            'The selected study and site are not valid. ' + 'Please enter a valid study and site.',
            {
              title: 'Error',
              variant: 'danger',
              solid: true,
              autoHideDelay: 5000,
            },
          );
        }
      },
      redirectBack() {
        this.$router.push({
          name: 'application-access-manager-manage-access',
        });
      },
      reset() {
        this.studyId = null;
        this.siteId = null;
      },
      linkClass(idx) {
        if (this.tabIndex === idx) {
          return ['bg-magnolia', 'text-black'];
        } else {
          return ['bg-customGrey', 'text-secondary'];
        }
      },
    },
  };
</script>

<style scoped>
  .textStyle {
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.5px;
    color: #006a68;
  }
  .headerStyle {
    font-weight: 500;
    font-size: 18px;
    line-height: 28px;
    letter-spacing: 0.25px;
    color: #1c1b1e;
  }
  .cardStyle {
    background: #fffbfe;
    box-shadow: 0px 4px 4px -2px rgb(23 38 74 / 8%);
    border-radius: 24px;
    letter-spacing: 0.25px;
  }
  @media (max-width: 992px) {
    .customPaddingInline {
      padding-inline: 10%;
    }
  }
  @media (min-width: 992px) {
    .customPaddingInline {
      padding-inline: 25%;
    }
  }
</style>
